import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Target, Plus, Trash2, Pencil, ArrowUp, ArrowDown, Loader2, X, Link2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";

const inputCls = "bg-white/[0.04] border-white/10";
const selectCls = "w-full h-9 rounded-md bg-white/[0.04] border border-white/10 text-sm px-2 text-slate-100";

export default function ObjectifsTypesPP() {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dlg, setDlg] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [ot, d] = await Promise.all([api.get("/pp/objective-types", { params: { all: true } }), api.get("/pp/domains")]);
      setRows(ot.data.objective_types || []);
      setDomains((d.data.domains || []).map((x) => x.label));
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => setDlg({ label: "", domain: "", secondary_domains: [], keywords: [], active: true });
  const openEdit = (r) => setDlg({ id: r.id, label: r.label, domain: r.domain || "", secondary_domains: r.secondary_domains || [], keywords: r.keywords || [], active: r.active !== false });

  const save = async () => {
    if (!dlg.label.trim()) return;
    setBusy(true);
    const payload = { label: dlg.label.trim(), domain: dlg.domain, secondary_domains: dlg.secondary_domains, keywords: dlg.keywords, active: dlg.active };
    try {
      if (dlg.id) await api.put(`/pp/objective-types/${dlg.id}`, payload);
      else await api.post("/pp/objective-types", payload);
      toast.success(t("pp.objTypes.saved")); setDlg(null); load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    setBusy(false);
  };
  const toggle = async (r) => {
    const next = !(r.active !== false);
    try { await api.put(`/pp/objective-types/${r.id}`, { active: next }); setRows((x) => x.map((y) => (y.id === r.id ? { ...y, active: next } : y))); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const del = async (r) => {
    if (!window.confirm(t("pp.objTypes.deleteConfirm"))) return;
    try { await api.delete(`/pp/objective-types/${r.id}`); toast.success(t("pp.objTypes.deleted")); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const relink = async () => {
    if (!window.confirm(t("pp.objTypes.relinkConfirm"))) return;
    try {
      const { data } = await api.post("/pp/objective-types/relink");
      toast.success(t("pp.objTypes.relinkResult", { linked: data.linked, remaining: data.unlinked_remaining }));
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const move = async (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= rows.length) return;
    const next = [...rows];
    [next[i], next[j]] = [next[j], next[i]];
    setRows(next);
    try { await api.post("/pp/objective-types/reorder", { order: next.map((x) => x.id) }); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const toggleSecondary = (label) => setDlg((d) => ({ ...d, secondary_domains: d.secondary_domains.includes(label) ? d.secondary_domains.filter((x) => x !== label) : [...d.secondary_domains, label] }));

  return (
    <div className="min-h-screen bg-[#0a1721] text-slate-100 p-4 sm:p-6" data-testid="objtypes-page">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-2 text-teal-400"><Target className="h-5 w-5" /><span className="text-xs font-semibold uppercase tracking-widest">CarePilot</span></div>
        <h1 className="text-3xl sm:text-4xl font-extrabold mt-1">{t("pp.objTypes.title")}</h1>
        <p className="text-slate-400 text-sm mt-1 mb-6">{t("pp.objTypes.subtitle")}</p>

        <div className="flex flex-wrap items-center gap-2 mb-5">
          <Button onClick={openNew} data-testid="objtype-add-btn" className="bg-teal-500 hover:bg-teal-400 text-slate-900 font-semibold"><Plus className="h-4 w-4 mr-1" />{t("pp.objTypes.add")}</Button>
          <Button onClick={relink} variant="outline" data-testid="objtype-relink-btn" className="border-teal-500/40 text-teal-300 hover:bg-teal-500/10"><Link2 className="h-4 w-4 mr-1" />{t("pp.objTypes.relink")}</Button>
        </div>

        {loading ? (
          <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-teal-400" /></div>
        ) : rows.length === 0 ? (
          <p className="text-slate-500 text-center py-10">{t("pp.objTypes.empty")}</p>
        ) : (
          <div className="space-y-2">
            {rows.map((r, i) => (
              <div key={r.id} className={`flex items-start gap-2 rounded-xl ring-1 ring-white/10 p-3 ${r.active !== false ? "bg-white/[0.04]" : "bg-white/[0.02] opacity-70"}`} data-testid={`objtype-row-${i}`}>
                <div className="flex flex-col pt-1">
                  <button onClick={() => move(i, -1)} disabled={i === 0} className="text-slate-500 hover:text-teal-300 disabled:opacity-30" data-testid={`objtype-up-${i}`}><ArrowUp className="h-3.5 w-3.5" /></button>
                  <button onClick={() => move(i, 1)} disabled={i === rows.length - 1} className="text-slate-500 hover:text-teal-300 disabled:opacity-30" data-testid={`objtype-down-${i}`}><ArrowDown className="h-3.5 w-3.5" /></button>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-100">{r.label}</div>
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {r.domain && <span className="rounded-full bg-teal-500/15 text-teal-300 px-2 py-0.5 text-[11px] font-semibold">{r.domain}</span>}
                    {(r.secondary_domains || []).map((d) => <span key={d} className="rounded-full bg-sky-500/10 text-sky-300 px-2 py-0.5 text-[11px]">{d}</span>)}
                  </div>
                  {(r.keywords || []).length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {r.keywords.map((k) => <span key={k} className="rounded bg-amber-500/10 text-amber-300 px-1.5 py-0.5 text-[10px]">{k}</span>)}
                    </div>
                  )}
                </div>
                <label className="flex items-center gap-1.5 text-xs text-slate-400 shrink-0 pt-1">
                  <Switch checked={r.active !== false} onCheckedChange={() => toggle(r)} data-testid={`objtype-active-${i}`} />
                </label>
                <button onClick={() => openEdit(r)} className="p-2 rounded-lg text-slate-400 hover:text-teal-300 hover:bg-white/5 shrink-0" data-testid={`objtype-edit-${i}`}><Pencil className="h-4 w-4" /></button>
                <button onClick={() => del(r)} className="p-2 rounded-lg text-slate-400 hover:text-red-300 hover:bg-white/5 shrink-0" data-testid={`objtype-del-${i}`}><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!dlg} onOpenChange={(o) => { if (!o) setDlg(null); }}>
        <DialogContent className="bg-[#0f2130] border-white/10 text-slate-100 max-w-lg max-h-[90vh] overflow-y-auto" data-testid="objtype-dialog">
          <DialogHeader><DialogTitle>{dlg?.id ? t("pp.objTypes.editTitle") : t("pp.objTypes.addTitle")}</DialogTitle></DialogHeader>
          {dlg && (
            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-slate-400 block mb-1">{t("pp.objTypes.label")}</label>
                <Input value={dlg.label} onChange={(e) => setDlg({ ...dlg, label: e.target.value })} className={inputCls} data-testid="objtype-label-input" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-400 block mb-1">{t("pp.objTypes.mainDomain")}</label>
                <select value={dlg.domain} onChange={(e) => setDlg({ ...dlg, domain: e.target.value })} className={selectCls} data-testid="objtype-domain-select">
                  <option value="" className="bg-[#0f2130]">{t("pp.selectDomain")}</option>
                  {domains.map((d) => <option key={d} value={d} className="bg-[#0f2130]">{d}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-400 block mb-1">{t("pp.objTypes.secondaryDomains")}</label>
                <div className="flex flex-wrap gap-1.5">
                  {domains.filter((d) => d !== dlg.domain).map((d) => (
                    <button key={d} type="button" onClick={() => toggleSecondary(d)}
                      className={`rounded-full px-2.5 py-1 text-[11px] font-medium border ${dlg.secondary_domains.includes(d) ? "bg-sky-500/20 text-sky-200 border-sky-500/40" : "bg-white/[0.03] text-slate-400 border-white/10"}`}>
                      {d}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-400 block mb-1">{t("pp.objTypes.keywords")}</label>
                <KeywordsInput value={dlg.keywords} onChange={(kw) => setDlg({ ...dlg, keywords: kw })} />
                <p className="text-[11px] text-slate-500 mt-1">{t("pp.objTypes.keywordsHint")}</p>
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-300">
                <Switch checked={dlg.active} onCheckedChange={(v) => setDlg({ ...dlg, active: v })} data-testid="objtype-active-switch" />
                {dlg.active ? t("pp.objTypes.active") : t("pp.objTypes.inactive")}
              </label>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDlg(null)}>{t("pp.cancel")}</Button>
            <Button onClick={save} disabled={busy || !dlg?.label?.trim()} data-testid="objtype-save-btn" className="bg-teal-500 hover:bg-teal-400 text-slate-900 font-semibold">{busy ? t("pp.saving") : t("pp.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KeywordsInput({ value, onChange }) {
  const [txt, setTxt] = useState("");
  const add = () => {
    const parts = txt.split(",").map((x) => x.trim()).filter(Boolean);
    if (parts.length) { onChange([...new Set([...value, ...parts])]); setTxt(""); }
  };
  return (
    <div>
      <div className="flex gap-2">
        <Input value={txt} onChange={(e) => setTxt(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} className={inputCls} data-testid="objtype-keyword-input" />
        <Button type="button" onClick={add} className="bg-white/10 hover:bg-white/20 shrink-0"><Plus className="h-4 w-4" /></Button>
      </div>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {value.map((k) => (
            <span key={k} className="inline-flex items-center gap-1 rounded bg-amber-500/15 text-amber-200 px-2 py-0.5 text-xs">
              {k}<button type="button" onClick={() => onChange(value.filter((x) => x !== k))}><X className="h-3 w-3" /></button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
